import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { color } from '@/colors';
import { CaretIcon } from '@/UI/Icons';
import { LoadingCircle } from '@/UI/Animations/LoadingCircle';
import { I18N_KEYS } from '@/language/keys';
import { useWorkflowStore } from '@/state/workflow.state';
import { FlexCenVer } from '@/UI/styles.ts';
import { useBatchesByRecipeId, useBatchesStore, useRunningBatchesByRecipeId } from '../batches.store';
import { isAnyRun } from '../batch.utils';
import { useFlowRunContext } from '../FlowRunContext';

export const BatchStatusButton = () => {
  const taskManagerButtonRef = useRef<HTMLButtonElement | null>(null);
  const markBatchesAsViewed = useBatchesStore((s) => s.markBatchesAsViewed);
  const isTaskManagerOpen = useBatchesStore((s) => s.isTaskManagerOpen);
  const setIsTaskManagerOpen = useBatchesStore((s) => s.setIsTaskManagerOpen);
  const { initializingBatchNodes } = useFlowRunContext();

  const recipeId = useWorkflowStore((state) => state.recipe.id);
  const batches = useBatchesByRecipeId(recipeId);

  const runningBatches = useRunningBatchesByRecipeId(recipeId);

  const { t } = useTranslation();
  const hasUnviewedErrors = useMemo(() => {
    return batches.filter((b) => !b.isViewed && isAnyRun(b, 'FAILED')).length > 0;
  }, [batches]);
  // if task manager open and there are unviewed errors, mark all batches as viewed immediately
  useEffect(() => {
    if (isTaskManagerOpen && hasUnviewedErrors) {
      markBatchesAsViewed();
    }
  }, [isTaskManagerOpen, hasUnviewedErrors, markBatchesAsViewed]);
  const { text, startIcon, errorIndicationDot } = useMemo(() => {
    if (runningBatches.length > 0) {
      return {
        text: (
          <>
            {t(I18N_KEYS.TASK_MANAGER.TASKS)}
            <Box component="span" sx={{ color: color.White64_T, pl: 0.5 }}>
              {t(I18N_KEYS.TASK_MANAGER.RUNNING_TEXT, { count: runningBatches.length })}
            </Box>
          </>
        ),
        errorIndicationDot: undefined,
      };
    } else if (Object.values(initializingBatchNodes).flat().length > 0) {
      return {
        text: t(I18N_KEYS.TASK_MANAGER.INITIALIZING),
        startIcon: <LoadingCircle color={color.White100} size={16} />,
        errorIndicationDot: undefined,
      };
    } else {
      return {
        text: t(I18N_KEYS.TASK_MANAGER.TASKS),
        startIcon: undefined,
        errorIndicationDot: hasUnviewedErrors ? (
          <Box
            className="error-indication-dot"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: color.Weavy_Error,
              position: 'absolute',
              top: 2,
              left: 39,
            }}
          />
        ) : undefined,
      };
    }
  }, [initializingBatchNodes, hasUnviewedErrors, runningBatches.length, t]);

  const handleClick = useCallback(() => {
    markBatchesAsViewed();
    setIsTaskManagerOpen(!isTaskManagerOpen);
  }, [markBatchesAsViewed, setIsTaskManagerOpen, isTaskManagerOpen]);

  return (
    <ButtonContained
      className="notranslate"
      ref={taskManagerButtonRef}
      disableRipple
      mode="text"
      size="small"
      data-task-manager-toggle
      sx={{
        height: 20,
        bgcolor: isTaskManagerOpen ? color.White08_T : 'unset',
        px: 0.75,
        '.error-indication-dot': {
          outline: `2px solid ${color.Black92}`,
        },
        '&:hover .error-indication-dot': {
          outline: `2px solid ${color.Black84}`,
        },
      }}
      startIcon={<FlexCenVer>{startIcon && <FlexCenVer>{startIcon}</FlexCenVer>}</FlexCenVer>}
      endIcon={
        <FlexCenVer sx={{ gap: 0.5 }}>
          {errorIndicationDot}
          <CaretIcon width={8} height={8} />
        </FlexCenVer>
      }
      onClick={handleClick}
    >
      {text}
    </ButtonContained>
  );
};
